import React, { useEffect, useState } from "react"

import styled from "@emotion/styled"
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
} from "@mui/material"
import SectionNameField from "./SectionNameField"
import {
  Tooltip,
} from "@mui/material"
import { ErrorMessage, } from "formik"

import { COLORS, roundNumber } from "../../../../utils"
import { StyledErrorMessage, StyledSectionFirstBodyColumn, StyledStepBodyCell, StyledStickyLastBodyColumn } from "../StyledSectionComponents"
import { computeSectionData, getDefaultSection, pushReusableProductionStepToSection } from "../../../../utils/recipes"
import { PRODUCTION_STEPS_COL_WIDTHS } from "../../../../utils/constant"
import MoreMenu from "../../../MoreMenu"
import { convertKilosIntoGrams } from "../../../../utils/ressources"
import StarBorderIcon from "@mui/icons-material/StarBorder"
import ReusableStepModal from "../../../../containers/Settings/TechnicalSheet/Resources/reusableSteps/ReusableStepModal"
import { useDispatch } from "react-redux"
import { sectionToReusableProductionStep } from "../../../../actions/Steps/reusableProductionSteps."
import { displaySectionName, getSectionNameStyles } from "../../../../utils/sectionUtils"

const widths = PRODUCTION_STEPS_COL_WIDTHS
export const COMPONENT_NAME = "SECTIONS"

const StyledText = styled(Box, {
  shouldForwardProp: (prop) => prop !== "disabled" && prop !== "packagingDisplay"
})(({ disabled = true, packagingDisplay = true }) => {
  let defaultStyles = {
    fontWeight: 600,
    fontSize: 14
  }

  if (disabled) {
    defaultStyles.color = packagingDisplay ? COLORS.BLACK_700 : COLORS.BLACK_500
  } else {
    defaultStyles.color = packagingDisplay ? COLORS.PRODUCTION_STEPS_TEXT_GREY : COLORS.BLACK_500
  }

  return defaultStyles
})

const EditableSection = ({
  sections,
  section,
  index,
  isHover,
  setFieldValue,
  onFieldFocus,
  onFieldBlur,
  onKeyUp,
  hasError,
  onDeleteBlur,
  computeSectionsFormValues,
  onDivideSection,
  handleMaskSectionInPackaging,
  onSectionNetWeightChange,
}) => {
  const [openModal, setOpenModal] = useState(false)
  const [isNetWeightHovered, setIsNetWeightHovered] = useState(false)
  const [isNetWeightMouseOver, setIsNetWeightMouseOver] = useState(false)
  const [inputValue, setInputValue] = useState("")

  const sectionUsesSectionOutput = section.productionSteps?.some((ps) => {
    if (!ps.reusable) {
      return ps.step?.stepComponents?.some(sc => !!sc.sectionOutput)
    }
    return ps.step?.productionSteps?.some(subStep =>
      subStep.stepComponents?.some(sc => !!sc.sectionOutput)
    )
  }) ?? false

  // Seed input when opening
  useEffect(() => {
    if (isNetWeightHovered) {
      setInputValue(String(roundNumber(convertKilosIntoGrams(section.netWeight), 2)))
    }
  }, [isNetWeightHovered])

  const handleNetWeightInputChange = (e) => {
    setInputValue(e.target.value.replace(",", "."))
  }

  const submitSectionNetWeight = () => {
    const parsed = parseFloat(inputValue)
    if (!isNaN(parsed) && isFinite(parsed) && parsed > 0) {
      const newNetWeightKg = parseFloat((parsed / 1000).toFixed(10))
      if (roundNumber(parsed, 2) !== roundNumber(convertKilosIntoGrams(section.netWeight), 2)) {
        onSectionNetWeightChange && onSectionNetWeightChange(index, newNetWeightKg)
      }
    }
    setIsNetWeightHovered(false)
  }

  const handleNetWeightKeyDown = (e) => {
    if (e.key === "Enter") {
      submitSectionNetWeight()
    } else if (e.key === "Escape") {
      setIsNetWeightHovered(false)
    }
  }

  const dispatch = useDispatch()

  const _stopPropagation = (event) => event && event.stopPropagation()

  const handleChangeSectionName = (sectionIndex) => (event) => {
    if (!event) return

    setFieldValue(`sections[${sectionIndex}].name`, event.target.value)
    _stopPropagation(event)
  }

  const handleAddSection = (index, event = null) => {
    const newSections = [...sections]
    newSections.splice(index + 1, 0, getDefaultSection())

    // update section with computed production steps and step components data
    const newSection = newSections[newSections.length - 1]
    if (newSection) {
      computeSectionData(newSection, "productionSteps", "stepComponents")
    }

    onDeleteBlur()
    setFieldValue("sections", newSections)
    _stopPropagation(event)
  }

  const _copySectionToReusableStep = async (section, event = null) => {
    const newSections = [...sections]
    const { newPs: newRecipeObject, reusableProductionSteps } = await dispatch(sectionToReusableProductionStep(section))
    const newRecipe = reusableProductionSteps.find((step) => step.objectId === newRecipeObject.id)
    const newSection = pushReusableProductionStepToSection({ ...section, productionSteps: [] }, { ...newRecipe, netWeight: newRecipeObject.netWeight, grossWeight: newRecipeObject.grossWeight }, 0)

    newSections.splice(index, 1, newSection)

    setFieldValue("sections", newSections)
    _stopPropagation(event)
  }

  const handleRemoveSection = (index, event = null) => {
    const newSections = [...sections]
    newSections.splice(index, 1)
    if (!newSections.length) {
      newSections.splice(0, 0, getDefaultSection())
    }

    computeSectionsFormValues(newSections, index > 0 ? index - 1 : 0)
    _stopPropagation(event)
  }

  const moreMenus = [
    {
      onClick: () => {
        onDivideSection && onDivideSection()
      },
      label: "Diviser la section",
      disabled: section.packagingDisplay === false,
      title: section.packagingDisplay === false ? "Veuillez démasquer la section au barquettage pour créer une division." : undefined
    },
    {
      onClick: (e) => handleRemoveSection(index, e),
      label: "Supprimer la section"
    },
    {
      onClick: () => handleMaskSectionInPackaging(index),
      label: section.packagingDisplay ? "Masquer au barquettage" : "Démasquer au barquettage",
      disabled: section.sectionOutputs && section.sectionOutputs.length > 0,
      title: section.sectionOutputs && section.sectionOutputs.length > 0 ? "Poids automatiquement géré par la division." : undefined
    }
  ]

  return (
    <Box sx={{ display: "flex" }} onClick={_stopPropagation}>
      <StyledSectionFirstBodyColumn className="flexRow center" packagingDisplay={section.packagingDisplay}>
        {isHover ? (
          <>
            {/* plus button */}
            <Button onClick={(e) => handleAddSection(index, e)} className="flexCenter" sx={{ position: "absolute", left: -8 }}>
              <img alt="plus icon" src="/icons/plus-blue.svg" />
            </Button>
            {/* input */}
            <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
              <SectionNameField
                value={section.name}
                index={index}
                setFieldValue={setFieldValue}
                onFieldFocus={onFieldFocus}
                onFieldBlur={onFieldBlur}
                onKeyUp={onKeyUp}
                variant="standard"
                fullWidth
                onChange={handleChangeSectionName(index)}
              />
              <ErrorMessage
                name={`sections[${index}].name`}
                render={(message) => (
                  <StyledErrorMessage>{message}</StyledErrorMessage>
                )}
              />
            </Stack>
            <div className="flexCenter" style={{ height: 15 }}>
              {
                isHover && !section.productionSteps.find(pS => pS.reusable) &&
                <Tooltip title="Transformer en étape réutilisable" placement="right">
                  <IconButton size="large" >
                    <StarBorderIcon
                      htmlColor={COLORS.PRODUCTION_STEPS_TEXT_GREY}
                      className="border"
                      onClick={(e) => {
                        _stopPropagation(e)
                        setOpenModal(true)
                      }}
                    />
                  </IconButton>
                </Tooltip>
              }
            </div>
          </>
        ) : (
          hasError(index)
            ? (
              <ErrorMessage
                name={`sections[${index}].name`}
                render={(message) => (
                  <StyledErrorMessage>{message}</StyledErrorMessage>
                )}
              />
            ) : (
              <Box sx={{ height: 30 }} className="flexRow center">
                <StyledText
                  disabled={false}
                  sx={getSectionNameStyles(section)}
                >
                  {displaySectionName(section)}
                </StyledText>
              </Box>
            )
        )}
      </StyledSectionFirstBodyColumn>
      <StyledStepBodyCell align="left" width={widths[1]}>
        <StyledText packagingDisplay={section.packagingDisplay}>{roundNumber(convertKilosIntoGrams(section.grossWeight), 3) || "-"}</StyledText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[2]}>
        <StyledText packagingDisplay={section.packagingDisplay}>-</StyledText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[3]}>
        <StyledText packagingDisplay={section.packagingDisplay}>
          {section.cost ? `${roundNumber(section.cost, 3)} €` : "_"}
        </StyledText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[4]}>
        <StyledText packagingDisplay={section.packagingDisplay}>-</StyledText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[5]}>
        <StyledText packagingDisplay={section.packagingDisplay}>-</StyledText>
      </StyledStepBodyCell>
      <StyledStepBodyCell
        align="left"
        width={widths[6]}
        onClick={() => !sectionUsesSectionOutput && !isNetWeightHovered && setIsNetWeightHovered(true)}
        onMouseEnter={() => !sectionUsesSectionOutput && !isNetWeightHovered && setIsNetWeightMouseOver(true)}
        onMouseLeave={() => setIsNetWeightMouseOver(false)}
        sx={!sectionUsesSectionOutput && !isNetWeightHovered ? { cursor: "pointer" } : {}}
      >
        {isNetWeightHovered ? (
          <TextField
            variant="standard"
            value={inputValue}
            onChange={handleNetWeightInputChange}
            onBlur={submitSectionNetWeight}
            onKeyDown={handleNetWeightKeyDown}
            autoFocus
            onFocus={(e) => e.target.select()}
            InputProps={{
              disableUnderline: true,
            }}
            inputProps={{ inputMode: "decimal", style: { fontSize: 14, fontWeight: 600, width: 60, border: "1px solid #474747", borderRadius: 4, padding: "2px 4px" } }}
            sx={{ background: "#fff", borderRadius: 1, px: 0 }}
          />
        ) : (
          <Tooltip
            title={sectionUsesSectionOutput ? "Poids non modifiable avec sortie de division" : ""}
            placement="top"
          >
            <StyledText
              packagingDisplay={section.packagingDisplay}
              sx={isNetWeightMouseOver ? { border: "1px solid #ccc", borderRadius: "4px", padding: "1px 6px", background: "#fff" } : {}}
            >
              {roundNumber(convertKilosIntoGrams(section.netWeight), 2) || "-"}
            </StyledText>
          </Tooltip>
        )}
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[7]}>
        <StyledText packagingDisplay={section.packagingDisplay}>-</StyledText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[8]}>
        <StyledText packagingDisplay={section.packagingDisplay}>-</StyledText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[9]}>
        <StyledText packagingDisplay={section.packagingDisplay}>-</StyledText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[10]}>
        <StyledText packagingDisplay={section.packagingDisplay}>-</StyledText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[11]}>
        <StyledText packagingDisplay={section.packagingDisplay}>-</StyledText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[12]}>
        <StyledText packagingDisplay={section.packagingDisplay}>-</StyledText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[13]}>
        <StyledText packagingDisplay={section.packagingDisplay}>-</StyledText>
      </StyledStepBodyCell>
      {/* -------- more menu -------- */}
      <StyledStickyLastBodyColumn width={widths[13]}>
        <MoreMenu menus={moreMenus} iconButtonProps={{ isSmall: true }} />
      </StyledStickyLastBodyColumn>
      <ReusableStepModal
        open={openModal}
        step={section}
        onDetach={_copySectionToReusableStep}
        isSection
        onClose={() => setOpenModal(false)}
      />
    </Box>
  )
}

export default EditableSection
